import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Snackbar,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import MuiAlert from "@mui/material/Alert";
import {
  getFeeScheduleForMedicationById,
  updateFeeScheduleForMedication,
} from "./../../ApiConfig.jsx";

const validationSchema = yup.object().shape({
  medication: yup.string().required("Service is required"),
  drugCategory: yup.string().required("Drug category name is required"),
  pharmacy: yup.string().required("Pharmacy name is required"),
  isCovered: yup.string().required("Covered is required"),
  price: yup
    .string()
    .required("Fee is required")
    .matches(/^\d+$/, "Fee must contain only numbers"),
});

function EditFeeScheduleForMedication() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setStatus] = useState("Inactive");
  const initialUserDetails = {
    medication: "",
    drugCategory: "",
    pharmacy: "",
    isCovered: "",
    price: "",
  };

  const [itemDetails, setItemDetails] = useState(initialUserDetails);
  const [errors, setErrors] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchItemData = async (id) => {
    try {
      const data = await getFeeScheduleForMedicationById(id);
      if (data.status==="OK") {
        console.log(data.data);
        setStatus(data.data.status);
        setItemDetails({
          medication: data.data.medication,
          pharmacy: data.data.pharmacy,
          drugCategory: data.data.drugCategory,
          isCovered: data.data.isCovered,
          price: data.data.price,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    fetchItemData(id);
  }, [id]);



  const validateField = (field, value) => {
    validationSchema
      .validateAt(field, { ...itemDetails, [field]: value })
      .then(() => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
      })
      .catch((error) => {
        setErrors((prevErrors) => ({ ...prevErrors, [field]: error.message }));
      });
  };

  const handleFieldChange = (field, value) => {
    setItemDetails((prevUserDetails) => ({
      ...prevUserDetails,
      [field]: value,
    }));
    validateField(field, value);
  };

  const updateHandler = async (userDetails) => {
    const hasErrors = Object.values(errors).some(
      (errorMessage) => errorMessage !== ""
    );

    if (hasErrors) {
      console.log("Errors found. Cannot update fee schedules for medication.");
      return;
    }

    console.log(userDetails);
    setIsLoading(true);
    const body = {
  ...userDetails,status
}
    try {
      const respondData = await updateFeeScheduleForMedication(id, body);
      console.log(respondData);
      if (respondData.status) {
        setAlertSeverity("success");
        setAlertMessage("Fee schedule edited successfully");
        setTimeout(() => {
          navigate("/feeScheduleForMedicationManagement");
        }, 2500);
      }
    } catch (err) {
      console.log(err);
      setAlertSeverity("error");
      setAlertMessage(`Failed to edit fee schedule: ${err.message}`);
    } finally {
      setOpenSnackbar(true);
      setIsLoading(false);
    }
  };

  return (
    <Box m="20px" height="70vh" overflow="auto" paddingRight="20px">
      <Header
        title={`Edit Fee Schedules For Medication ${id}`}
        subtitle=""
      />
      <Box ml={"40px"}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Medication*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.medication}
              onChange={(e) => handleFieldChange("medication", e.target.value)}
              error={!!errors.medication}
              helperText={errors.medication}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Pharmacy*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.pharmacy}
              onChange={(e) =>
                handleFieldChange("pharmacy", e.target.value)
              }
              error={!!errors.pharmacy}
              helperText={errors.pharmacy}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Drug category*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.drugCategory}
              onChange={(e) =>
                handleFieldChange("drugCategory", e.target.value)
              }
              error={!!errors.drugCategory}
              helperText={errors.drugCategory}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Covered*
            </Typography>
            <FormControl fullWidth variant="filled">
              <Select
                value={itemDetails.isCovered}
                onChange={(e) => handleFieldChange("isCovered", e.target.value)}
                name="isCovered"
                error={!!errors.isCovered}>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {errors.isCovered && (
                <Typography variant="caption" color="error">
                  {errors.isCovered}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              Fee*
            </Typography>
            <TextField
              type="text"
              fullWidth
              variant="filled"
              value={itemDetails.price}
              onChange={(e) => handleFieldChange("price", e.target.value)}
              error={!!errors.price}
              helperText={errors.price}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#6870fa",
                color: "white",
                fontSize: "16px",
                "&:hover": {
                  backgroundColor: "#3e4396",
                },
              }}
              onClick={() => updateHandler(itemDetails)}>
              {isLoading ? "Updating..." : "Update"}
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <MuiAlert
          onClose={() => setOpenSnackbar(false)}
          severity={alertSeverity}
          elevation={6}
          variant="filled"
          style={{ color: "white" }}>
          {alertSeverity === "success" ? "Success" : "Error"}
          {": "}
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

export default EditFeeScheduleForMedication;
